import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiClient as api } from "../api/client";

const STATUSES = ["created", "sent", "contacted", "converted", "rejected"];
const STATUS_STYLES = {
  created: "border-sky-500/50 bg-sky-950/50 text-sky-200",
  sent: "border-indigo-500/50 bg-indigo-950/50 text-indigo-200",
  contacted: "border-amber-500/50 bg-amber-950/50 text-amber-200",
  converted: "border-emerald-500/50 bg-emerald-950/50 text-emerald-200",
  rejected: "border-rose-500/50 bg-rose-950/50 text-rose-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-AT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminLeadsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-leads", statusFilter, sourceFilter],
    queryFn: async () => {
      const { data } = await api.get("/admin/leads", {
        params: {
          limit: 200,
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(sourceFilter ? { is_guest: sourceFilter === "guest" } : {}),
        },
      });
      return data;
    },
  });

  const patchMut = useMutation({
    mutationFn: async ({ leadUlid, status }) => {
      const { data } = await api.patch(`/admin/leads/${leadUlid}`, { status });
      return data;
    },
    onSuccess: () => {
      toast.success("Lead-Status aktualisiert");
      qc.invalidateQueries({ queryKey: ["admin-leads"] });
    },
    onError: () => toast.error("Status-Update fehlgeschlagen"),
  });

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="admin-leads-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Partner Leads</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Finanzierungs- & Partneranfragen — {data?.total ?? "…"} gesamt.
            Neue Anfragen gehen zusätzlich per E-Mail an das Admin-Postfach.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            data-testid="leads-source-filter"
            className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200"
          >
            <option value="">Alle Herkünfte</option>
            <option value="guest">Nur Gäste (ohne Konto)</option>
            <option value="account">Nur angemeldete Nutzer</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="leads-status-filter"
            className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200"
          >
            <option value="">Alle Status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && <div className="text-sm text-zinc-500">Lade Leads…</div>}
      {error && (
        <div className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300">
          Leads konnten nicht geladen werden.
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500" data-testid="leads-empty">
          Keine Leads{statusFilter ? ` mit Status „${statusFilter}"` : ""} vorhanden.
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-zinc-800">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/60 text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2">Herkunft</th>
                <th className="px-3 py-2">Typ</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">E-Mail</th>
                <th className="px-3 py-2">Telefon</th>
                <th className="px-3 py-2">Listing</th>
                <th className="px-3 py-2">Land</th>
                <th className="px-3 py-2">Partner</th>
                <th className="px-3 py-2">Übergabe</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => {
                const contact = lead?.payload?.user_contact || {};
                return (
                  <tr
                    key={lead.lead_ulid}
                    className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                    data-testid={`lead-row-${lead.lead_ulid}`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{fmtDate(lead.created_at)}</td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`lead-source-${lead.lead_ulid}`}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                          lead.is_guest
                            ? "border-amber-500/50 bg-amber-950/40 text-amber-200"
                            : "border-zinc-700 bg-zinc-900 text-zinc-400"
                        }`}
                      >
                        {lead.is_guest ? "Gast" : "Konto"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-zinc-300">
                      {lead.lead_type}
                      <span className="text-zinc-600"> / {lead.vertical}</span>
                    </td>
                    <td className="px-3 py-2 text-zinc-200">{contact.name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-300">
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="text-emerald-300 hover:underline">{contact.email}</a>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-zinc-300">{contact.phone || "—"}</td>
                    <td className="px-3 py-2">
                      {lead.listing_ulid ? (
                        <Link to={`/listing/${lead.listing_ulid}`} className="text-emerald-300 hover:underline">
                          {String(lead.listing_ulid).slice(0, 10)}…
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-zinc-400">{lead.country || "—"}</td>
                    <td className="px-3 py-2 text-zinc-400">
                      {lead.partner || "—"}
                      {lead?.partner_routing?.match_reason?.startsWith(
                        "country_not_covered",
                      ) && (
                        <div className="text-[10px] text-amber-400/80">
                          {lead.partner_routing.match_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`lead-handoff-${lead.lead_ulid}`}
                        className="rounded-full border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-400"
                        title={lead?.handoff?.reason || ""}
                      >
                        {lead?.handoff?.status === "disabled_missing_api_docs"
                          ? "nicht übertragen"
                          : lead?.handoff?.status || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={lead.status}
                        onChange={(e) => patchMut.mutate({ leadUlid: lead.lead_ulid, status: e.target.value })}
                        data-testid={`lead-status-select-${lead.lead_ulid}`}
                        className={`rounded-full border px-2 py-1 text-xs font-medium bg-transparent ${STATUS_STYLES[lead.status] || "border-zinc-700 text-zinc-300"}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-zinc-950 text-zinc-200">{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
